import { Controller } from "@hotwired/stimulus"

// Premium tooltip interactions for Salary-by-Role range rows
// - Click a row to pin/unpin tooltip
// - Esc or outside click unpins
// - Enter/Space toggles pin for keyboard users
export default class extends Controller {
  static targets = ["tooltip"]

  connect() {
    this.pinnedRowId = null
    this.onWindowResize = this.onWindowResize.bind(this)
    this.onDocumentClick = this.onDocumentClick.bind(this)
    this.onDocumentKeydown = this.onDocumentKeydown.bind(this)

    document.addEventListener("click", this.onDocumentClick, true)
    document.addEventListener("keydown", this.onDocumentKeydown, true)
    window.addEventListener("resize", this.onWindowResize, { passive: true })
  }

  disconnect() {
    document.removeEventListener("click", this.onDocumentClick, true)
    document.removeEventListener("keydown", this.onDocumentKeydown, true)
    window.removeEventListener("resize", this.onWindowResize)
  }

  onKeydown(event) {
    // delegated from data-action
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault()
      this.toggle(event)
    }
  }

  toggle(event) {
    const row = event.currentTarget
    if (!row) return

    const rowId = row.dataset.salaryRangeRowId
    if (!rowId) return

    if (this.pinnedRowId === rowId) {
      this.unpin()
    } else {
      this.pin(rowId)
    }
  }

  pin(rowId) {
    this.unpin()
    this.pinnedRowId = rowId

    const row = this.element.querySelector(`[data-salary-range-row-id="${CSS.escape(rowId)}"]`)
    if (row) {
      row.classList.add("is-pinned")
      row.setAttribute("aria-expanded", "true")
    }

    this.tooltipTargets.forEach((el) => {
      if (el.dataset.salaryRangeTooltipFor === rowId) {
        el.setAttribute("aria-hidden", "false")
      } else {
        el.setAttribute("aria-hidden", "true")
      }
    })

    this.positionTooltip(rowId)
  }

  unpin() {
    if (!this.pinnedRowId) {
      // still ensure aria attributes are consistent
      this.tooltipTargets.forEach((el) => el.setAttribute("aria-hidden", "true"))
      return
    }

    const prevId = this.pinnedRowId
    this.pinnedRowId = null

    const row = this.element.querySelector(`[data-salary-range-row-id="${CSS.escape(prevId)}"]`)
    if (row) {
      row.classList.remove("is-pinned")
      row.setAttribute("aria-expanded", "false")
    }

    this.tooltipTargets.forEach((el) => el.setAttribute("aria-hidden", "true"))
  }

  onWindowResize() {
    if (!this.pinnedRowId) return
    this.positionTooltip(this.pinnedRowId)
  }

  positionTooltip(rowId) {
    const tooltip = this.tooltipTargets.find((el) => el.dataset.salaryRangeTooltipFor === rowId)
    if (!tooltip) return

    // Reset shift first so we measure true centered position
    tooltip.style.setProperty("--salary-tooltip-shift-x", "0px")

    // Use RAF so DOM has applied pin styles
    requestAnimationFrame(() => {
      const rect = tooltip.getBoundingClientRect()
      const padding = 12
      const leftOverflow = padding - rect.left
      const rightOverflow = rect.right - (window.innerWidth - padding)

      let shift = 0
      if (leftOverflow > 0) shift = leftOverflow
      if (rightOverflow > 0) shift = -rightOverflow

      tooltip.style.setProperty("--salary-tooltip-shift-x", `${shift}px`)
    })
  }

  onDocumentClick(event) {
    // If nothing pinned, ignore
    if (!this.pinnedRowId) return

    // Click inside the salary-range section should not auto-unpin
    if (this.element.contains(event.target)) return

    this.unpin()
  }

  onDocumentKeydown(event) {
    if (event.key !== "Escape") return
    if (!this.pinnedRowId) return
    this.unpin()
  }
}



